import React from 'react';
import classNames from 'classnames';
import { useSelect } from 'downshift';

import { CheckboxRadioInput } from '../checkbox-radio-input';
import { FieldLabel } from '../field';
import { ListboxCommonProps } from './listbox.model';
import { multiselectStateReducer, nonNullItemToString } from './listbox.util';
import { ListboxButton } from './listbox-button.component';
import { ListboxOption } from './listbox-option.component';
import { ListboxOptions } from './listbox-options.component';

export type ListboxMultipleProps<T> = ListboxCommonProps<T> & {
  value?: T[];
  showSelectedItemsBadge?: boolean;
  onChange?: (selectedItems: T[]) => void;
};

export function ListboxMultiple<T>({
  label,
  items,
  value = [],
  disabled,
  hasError,
  required,
  showRequiredIndicator = true,
  showSelectedItemsBadge = false,
  fullWidth = true,
  placeholder = 'Select',
  truncate,
  variant,
  height,
  errorId,
  className,
  itemToString,
  onChange,
  onMenuBlur,
}: ListboxMultipleProps<T>) {
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect<T>({
    items,
    itemToString: nonNullItemToString(itemToString),
    stateReducer: multiselectStateReducer,
    selectedItem: null,
    onStateChange: (changes) => {
      if (changes.type === useSelect.stateChangeTypes.MenuBlur && onMenuBlur) {
        onMenuBlur();
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem || !onChange) {
        return;
      }

      const index = value.indexOf(selectedItem);
      if (index > 0) {
        onChange([...value.slice(0, index), ...value.slice(index + 1)]);
      } else if (index === 0) {
        onChange([...value.slice(1)]);
      } else {
        onChange([...value, selectedItem]);
      }
    },
  });

  const showValue = !showSelectedItemsBadge && value.length;
  const buttonText = showValue ? `${value.length} selected` : placeholder;

  return (
    <>
      <FieldLabel
        {...getLabelProps()}
        hasError={hasError}
        showRequiredIndicator={required && showRequiredIndicator}
      >
        {label}
      </FieldLabel>

      <div
        className={classNames('relative inline-block', {
          'w-full': fullWidth,
        })}
      >
        <ListboxButton
          disabled={disabled}
          hasError={hasError}
          fullWidth={fullWidth}
          truncate={truncate}
          variant={variant}
          height={height}
          showSelectedItemsBadge={showSelectedItemsBadge}
          selectedItemsCount={value.length}
          isPlaceholder={!showValue}
          className={className}
          {...getToggleButtonProps({
            'aria-required': required,
            'aria-describedby': errorId,
          })}
        >
          {buttonText}
        </ListboxButton>
        <ListboxOptions
          isOpen={isOpen}
          fullWidth={fullWidth}
          {...getMenuProps()}
        >
          {isOpen &&
            items.map((item, index) => (
              <ListboxOption
                key={`${item}${index}`}
                highlighted={highlightedIndex === index}
                {...getItemProps({
                  item,
                  index,
                })}
              >
                <CheckboxRadioInput
                  checked={value.includes(item)}
                  value={nonNullItemToString(itemToString)(item)}
                  tabIndex={-1}
                  className="flex-shrink-0 mr-3 pointer-events-none"
                  onChange={() => null}
                />
                <div
                  className={classNames({
                    truncate,
                  })}
                >
                  {nonNullItemToString(itemToString)(item)}
                </div>
              </ListboxOption>
            ))}
        </ListboxOptions>
      </div>
    </>
  );
}
